import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { getNewsById, newsList, fmtDate } from "@/mocks/anime";

export default function NewsDetailPage() {
  const { id } = useParams();
  const news = getNewsById(id);

  usePageMeta(
    news ? `妖精动漫 - 资讯《${news.title.slice(0, 22)}》` : "妖精动漫 - 资讯未找到",
    news ? `${news.summary.slice(0, 100)} 妖精动漫资讯频道，关注动漫作品动态与行业观察。` : "抱歉，没有找到对应的资讯内容。"
  );

  if (!news) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
            <i className="ri-file-warning-line text-2xl" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-950">没有找到这条资讯</h1>
          <p className="mt-2 text-[13px] text-foreground-500">链接可能已失效，去资讯列表看看最新内容吧。</p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center h-10 px-5 rounded-md bg-primary-900 text-background-50 text-[13px] font-semibold whitespace-nowrap cursor-pointer"
          >
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = newsList.filter((item) => item.id !== news.id).slice(0, 3);
  const sameCategory = newsList.filter((item) => item.id !== news.id && item.category === news.category);

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 py-8 md:py-10">
          <div className="[&_a]:text-primary-300 [&_span]:text-primary-100">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫资讯", to: "/news" }, { label: news.category }]} />
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="px-2 py-0.5 rounded bg-accent-500 text-primary-950 font-bold">{news.category}</span>
            <span className="text-primary-300 flex items-center gap-1"><i className="ri-calendar-line" /> {news.date}</span>
            <span className="text-primary-300 flex items-center gap-1"><i className="ri-user-3-line" /> {news.author}</span>
          </div>
          <h1 className="mt-3 text-2xl md:text-[32px] font-black leading-snug tracking-tight text-background-50 max-w-[900px]">
            {news.title}
          </h1>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 lg:gap-10">
          <article className="min-w-0">
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img
                src={news.cover}
                alt={`${news.title}正文配图`}
                title={`${news.title} - 妖精动漫资讯`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-6 rounded-lg bg-background-100 border-l-2 border-accent-500 px-4 py-3">
              <p className="text-[13px] leading-6 text-foreground-700">{news.summary}</p>
            </div>

            <div className="mt-6 space-y-5">
              {news.content.map((block, i) => (
                <div key={i}>
                  {block.heading && (
                    <h2 className="text-[16px] md:text-[18px] font-bold text-foreground-950 mb-2 tracking-tight">
                      {block.heading}
                    </h2>
                  )}
                  <p className="text-[13px] md:text-[14px] leading-8 text-foreground-700">{block.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-background-200">
              <div className="flex flex-wrap gap-1.5">
                {["动漫资讯", news.category, "妖精动漫"].map((tag) => (
                  <span key={tag} className="px-2 py-1 rounded bg-background-100 text-[11px] text-foreground-600">
                    # {tag}
                  </span>
                ))}
              </div>
              <Link
                to="/news"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-background-300 text-foreground-700 text-[12px] font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" /> 返回资讯列表
              </Link>
            </div>
          </article>

          <aside className="lg:sticky lg:top-[88px] lg:self-start space-y-4">
            <div className="rounded-lg bg-background-100 border border-background-200 p-4">
              <h2 className="text-[13px] font-bold text-foreground-950 mb-3">相关推荐资讯</h2>
              <div className="space-y-3">
                {related.map((item) => (
                  <Link key={item.id} to={`/news/detail/${item.id}`} className="group block cursor-pointer">
                    <div className="w-full aspect-[16/9] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title}推荐配图`}
                        title={`${item.title} - 相关资讯`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <p className="mt-2 text-[12px] font-medium text-foreground-800 leading-5 line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-400">{fmtDate(item.date)} · {item.category}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-4">
              <h2 className="text-[13px] font-bold text-secondary-950">
                {sameCategory.length > 0 ? `更多「${news.category}」` : "资讯分类"}
              </h2>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {newsList.slice(0, 6).map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/detail/${item.id}`}
                    className="px-2 py-1 rounded bg-background-50 text-[11px] text-secondary-900 border border-secondary-200 hover:border-secondary-400 transition-colors cursor-pointer"
                  >
                    {item.category}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}